import { getWidgetById, type Widget } from "@/utils/widgets";
import {
  remapWidgetIds,
  validateLayoutData,
  type LayoutExportData,
} from "@/utils/layoutExportImport";
import type {
  BreakpointLayouts,
  WidgetFilterData,
} from "@/utils/DashboardLayouts";
import useTabStore from "@/stores/useTabStore";

export type ApplyLayoutOptions = {
  /** Access predicate from useAccessControl — inaccessible widgets are dropped. */
  hasAccessToWidget: (widget: Widget) => boolean;
  /**
   * Starter watchlist (Portfolio) IDs to merge into the primary
   * NewsquawkHeadlines instance as HIGHLIGHTS — never filters. The main desk
   * wire must keep flowing in full; highlighting emphasises the user's names
   * without hiding market context.
   */
  highlightWatchlistIds?: number[];
  /** Override the tab title from the layout data. */
  title?: string;
};

export type ApplyLayoutResult = {
  tabTitle: string;
  appliedWidgets: string[];
  /** Base ids dropped because the user lacks access (or unknown ids). */
  droppedWidgets: string[];
};

function baseIdOf(id: string): string {
  if (getWidgetById(id)) return id;
  const dashIdx = id.indexOf("-");
  return dashIdx === -1 ? id : id.substring(0, dashIdx);
}

const HIGHLIGHT_SECTION = "ticker-watchlists-highlight";
const PRIMARY_HIGHLIGHT_BASE = "NewsquawkHeadlines";

/**
 * The single ingestion path for generated dashboards — starter templates and
 * concierge plans both come through here. Validates, access-filters, merges
 * starter-watchlist highlights, remaps ids and imports the tab (which the
 * tab store auto-persists).
 *
 * Throws Error with a user-presentable message on invalid data.
 */
export function applyLayoutData(
  data: LayoutExportData,
  options: ApplyLayoutOptions
): ApplyLayoutResult {
  const validationError = validateLayoutData(data);
  if (validationError) {
    throw new Error(`Invalid layout data: ${validationError}`);
  }

  // 1. Drop widgets the user can't fully use (and unknown ids — validate
  //    accepts composite ids whose base exists, so re-check via registry).
  const droppedWidgets: string[] = [];
  const enabledWidgets = data.enabledWidgets.filter((id) => {
    const widget = getWidgetById(baseIdOf(id));
    if (widget && options.hasAccessToWidget(widget)) return true;
    droppedWidgets.push(baseIdOf(id));
    return false;
  });

  if (enabledWidgets.length === 0) {
    throw new Error(
      "None of this template's widgets are available on your plan."
    );
  }

  const enabledSet = new Set(enabledWidgets);
  const layout = {} as BreakpointLayouts;
  for (const bp of ["lg", "md", "sm", "xs", "xxs"] as const) {
    layout[bp] = (data.layout[bp] ?? []).filter((item) =>
      enabledSet.has(item.i)
    );
  }

  // 2. Keep only filters for surviving widgets.
  let widgetFilters: Record<string, WidgetFilterData> | undefined;
  if (data.widgetFilters) {
    widgetFilters = {};
    for (const [id, filterData] of Object.entries(data.widgetFilters)) {
      if (enabledSet.has(id)) widgetFilters[id] = filterData;
    }
  }

  // 3. Merge starter watchlists into the primary Headlines instance as
  //    highlights (first NewsquawkHeadlines in the widget order).
  if (options.highlightWatchlistIds?.length) {
    const primaryId = enabledWidgets.find(
      (id) => baseIdOf(id) === PRIMARY_HIGHLIGHT_BASE
    );
    if (primaryId) {
      widgetFilters = widgetFilters ?? {};
      const existing = widgetFilters[primaryId] ?? {};
      const existingHighlights = existing.highlight?.[HIGHLIGHT_SECTION] ?? [];
      widgetFilters[primaryId] = {
        ...existing,
        highlight: {
          ...existing.highlight,
          [HIGHLIGHT_SECTION]: [
            ...new Set([
              ...existingHighlights,
              ...options.highlightWatchlistIds.map(String),
            ]),
          ],
        },
      };
    }
  }

  // 4. Fresh composite ids, then import (auto-persisted by the tab store).
  const remapped = remapWidgetIds({ enabledWidgets, layout, widgetFilters });
  const tabTitle = options.title ?? data.tabTitle ?? "My Dashboard";
  useTabStore
    .getState()
    .importTab(
      tabTitle,
      remapped.enabledWidgets,
      remapped.layout,
      remapped.widgetFilters
    );

  return {
    tabTitle,
    appliedWidgets: remapped.enabledWidgets,
    droppedWidgets,
  };
}
